/**
 * MietwagenCrossLinkCard — Issue #272
 * ------------------------------------
 * Cross-link UI shown inside the Gutachten-Akten-Detail view to surface (and
 * manage) the link between a Gutachten-Akte and its corresponding Mietwagen-
 * Akte at MietGo.
 *
 * Four rendering modes driven by deriveMietwagenCrossLinkMode():
 *
 *   inactive        — Checkbox "Mietwagen in Anspruch genommen" is off: renders
 *                     only the checkbox (no banner, no buttons).
 *
 *   unlinked_active — Checkbox is on, no link, no suggestions: shows action
 *                     buttons "+ Mietwagen-Akte anlegen" and
 *                     "+ Mit bestehender verknüpfen".
 *
 *   suggestions     — Checkbox is on, no link, suggestions loaded from API:
 *                     shows a banner per candidate (Aktenzeichen, Kunde,
 *                     Tagessatz) with a "Verknüpfen" button each.
 *
 *   linked          — mietwagen_akte_id is set: shows a header chip with a
 *                     deep link to the Mietwagen-Akte.
 *
 * API calls (POST /api/akten-mietwagen-link):
 *   matchVorschlaege — load candidates (called when checkbox turns on)
 *   linkMietwagen    — confirm a candidate or picker selection
 *   unlinkMietwagen  — remove the link when checkbox is turned off
 *
 * The icon shown in the StickyAktenkopfzeile when linked is handled by
 * StickyAktenkopfzeile itself (reads akte.mietwagen_akte_id from props) — this
 * component only manages the card below the sticky header.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { Car, ExternalLink, Loader2, Plus, Link2, ArrowRight, Mail, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';
import {
  deriveMietwagenCrossLinkMode,
  buildMietwagenNeuanlageUrl,
  checkVollstaendigkeit,
  type MietwagenVorschlag,
} from '@/lib/mietwagen-crosslink';
import { StatusPill } from '@/components/ui/StatusPill';
import { berechneMietkosten } from '@/lib/mietwagen-kpi';
import DataCompletenessModal from './DataCompletenessModal';

interface LinkedMietwagenAkte {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  kunde_name: string | null;
  mietwagen_status: string | null;
  mietbeginn: string | null;
  mietende: string | null;
  tagespreis: number | null;
  unterschrift_anfrage_datum: string | null;
}

export interface MietwagenCrossLinkCardProps {
  gutachtenAkteId: string;
  mietwagenInAnspruch: boolean | null | undefined;
  mietwagenAkteId: string | null | undefined;
  kundeName: string | null | undefined;
  schadennummer: string | null | undefined;
  schadendatum: string | null | undefined;
  /** E-Mail des Kunden für die Unterschriften-Anfrage (optional; wird ggf. per Prompt abgefragt). */
  kundeEmail?: string | null;
  /** Called after any state-mutating API call so the parent can refetch the akte. */
  onAkteUpdated?: () => void;
}

export default function MietwagenCrossLinkCard({
  gutachtenAkteId,
  mietwagenInAnspruch,
  mietwagenAkteId,
  kundeName,
  schadennummer,
  schadendatum,
  kundeEmail,
  onAkteUpdated,
}: MietwagenCrossLinkCardProps) {
  const [vorschlaege, setVorschlaege] = useState<MietwagenVorschlag[]>([]);
  const [loadingVorschlaege, setLoadingVorschlaege] = useState(false);
  const [linked, setLinked] = useState<LinkedMietwagenAkte | null>(null);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDataModal, setShowDataModal] = useState<string[] | null>(null);
  const [, navigate] = useLocation();

  // Guard against re-fetch loops when the parent re-renders without changing ids.
  const lastFetchedLinkedId = useRef<string | null>(null);
  const lastFetchedVorschlaegeForAkte = useRef<string | null>(null);
  // Unmount guard for the "Mit bestehender verknüpfen" button fetch.
  const linkExistingCancelledRef = useRef(false);
  useEffect(() => {
    linkExistingCancelledRef.current = false;
    return () => { linkExistingCancelledRef.current = true; };
  }, []);

  // Fetch the linked mietwagen akte details when mietwagenAkteId changes.
  useEffect(() => {
    const id = mietwagenAkteId || null;
    if (lastFetchedLinkedId.current === id) return;
    lastFetchedLinkedId.current = id;

    if (!id) {
      setLinked(null);
      setLoadingLinked(false);
      return;
    }

    let cancelled = false;
    setLoadingLinked(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('akten')
          .select('id, akten_nummer, akten_nr, kunde_name, mietwagen_status, mietbeginn, mietende, tagespreis, unterschrift_anfrage_datum')
          .eq('id', id)
          .maybeSingle();
        if (error) throw error;
        if (cancelled) return;
        setLinked((data as LinkedMietwagenAkte | null) ?? null);
      } catch {
        if (!cancelled) setLinked(null);
      } finally {
        if (!cancelled) setLoadingLinked(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mietwagenAkteId]);

  // Fetch match suggestions when checkbox is active and no link exists.
  useEffect(() => {
    const shouldFetch =
      mietwagenInAnspruch &&
      !mietwagenAkteId &&
      gutachtenAkteId &&
      lastFetchedVorschlaegeForAkte.current !== gutachtenAkteId;

    if (!shouldFetch) return;
    lastFetchedVorschlaegeForAkte.current = gutachtenAkteId;

    let cancelled = false;
    setLoadingVorschlaege(true);
    (async () => {
      try {
        const res = await fetch('/api/akten-mietwagen-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'matchVorschlaege', gutachten_akte_id: gutachtenAkteId }),
        });
        if (cancelled) return;
        if (!res.ok) throw new Error(await res.text());
        const json = await res.json();
        setVorschlaege((json.vorschlaege ?? []) as MietwagenVorschlag[]);
      } catch (err) {
        if (!cancelled) {
          setVorschlaege([]);
          toast.error('Fehler beim Laden der Vorschläge', {
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
          });
        }
      } finally {
        if (!cancelled) setLoadingVorschlaege(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mietwagenInAnspruch, mietwagenAkteId, gutachtenAkteId]);

  const mode = deriveMietwagenCrossLinkMode(mietwagenInAnspruch, mietwagenAkteId, vorschlaege);

  async function handleCheckboxChange(checked: boolean) {
    if (saving) return;
    setSaving(true);
    try {
      if (!checked) {
        // Always clear stale suggestions when unchecking (Fix 3).
        setVorschlaege([]);
        if (mietwagenAkteId) {
          // Unlink AND clear mietwagen_in_anspruch in one server-side call to
          // avoid a dual-write race where the unlink succeeds but the flag
          // write silently fails (Fix 5).
          const res = await fetch('/api/akten-mietwagen-link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'unlinkAndClear', gutachten_akte_id: gutachtenAkteId }),
          });
          if (!res.ok) throw new Error(await res.text());
          lastFetchedLinkedId.current = null;
          setLinked(null);
        } else {
          // No link — just update the flag (single write, no race risk).
          const { error: errOff } = await supabase
            .from('akten')
            .update({ mietwagen_in_anspruch: false, updated_at: new Date().toISOString() })
            .eq('id', gutachtenAkteId);
          if (errOff) throw errOff;
        }
      } else {
        // Checking on — validate completeness first.
        const { ok, fehlend } = checkVollstaendigkeit({ kunde_name: kundeName, schadennummer });
        if (!ok) {
          // Show modal to collect missing fields; DB update happens after modal submit.
          setSaving(false);
          setShowDataModal(fehlend);
          return;
        }
        // All data present — update flag and reset guard so next render fetches suggestions.
        const { error: errOn } = await supabase
          .from('akten')
          .update({ mietwagen_in_anspruch: true, updated_at: new Date().toISOString() })
          .eq('id', gutachtenAkteId);
        if (errOn) throw errOn;
        lastFetchedVorschlaegeForAkte.current = null;
      }
      onAkteUpdated?.();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
      onAkteUpdated?.();
    } finally {
      setSaving(false);
    }
  }

  async function handleLinkVorschlag(mietwagenId: string) {
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch('/api/akten-mietwagen-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'linkMietwagen',
          gutachten_akte_id: gutachtenAkteId,
          mietwagen_akte_id: mietwagenId,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      lastFetchedLinkedId.current = null;
      setVorschlaege([]);
      onAkteUpdated?.();
    } catch (err) {
      toast.error('Fehler beim Verknüpfen', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
      onAkteUpdated?.();
    } finally {
      setSaving(false);
    }
  }

  async function activateCheckbox() {
    if (saving) return;
    setSaving(true);
    try {
      const { error: errOn } = await supabase
        .from('akten')
        .update({ mietwagen_in_anspruch: true, updated_at: new Date().toISOString() })
        .eq('id', gutachtenAkteId);
      if (errOn) throw errOn;
      lastFetchedVorschlaegeForAkte.current = null;
      onAkteUpdated?.();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
      onAkteUpdated?.();
    } finally {
      setSaving(false);
    }
  }

  function handleCreateNew() {
    const url = buildMietwagenNeuanlageUrl({ kundeName, schadennummer, schadendatum });
    navigate(url);
  }

  async function handleSignatureRequest() {
    if (!linked || saving) return;
    const email = kundeEmail?.trim() || window.prompt('E-Mail-Adresse des Kunden für die Unterschriften-Anfrage:')?.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Ungültige E-Mail-Adresse');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/signature-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          akte_id: gutachtenAkteId,
          mietwagen_akte_id: linked.id,
          recipient_email: email,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as Record<string, string>;
        throw new Error(err.error || 'Unbekannter Fehler');
      }
      toast.success('Unterschriften-Anfrage gesendet', {
        description: `Mail an ${email} verschickt.`,
      });
      lastFetchedLinkedId.current = null;
      onAkteUpdated?.();
    } catch (err) {
      toast.error('Fehler beim Senden der Unterschriften-Anfrage', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleLinkExisting() {
    linkExistingCancelledRef.current = false;
    lastFetchedVorschlaegeForAkte.current = null;
    setLoadingVorschlaege(true);
    try {
      const r = await fetch('/api/akten-mietwagen-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'matchVorschlaege', gutachten_akte_id: gutachtenAkteId }),
      });
      if (linkExistingCancelledRef.current) return;
      if (!r.ok) throw new Error(await r.text());
      const json = await r.json();
      if (!linkExistingCancelledRef.current) {
        setVorschlaege((json.vorschlaege ?? []) as MietwagenVorschlag[]);
        lastFetchedVorschlaegeForAkte.current = gutachtenAkteId;
      }
    } catch (err) {
      if (!linkExistingCancelledRef.current) {
        setVorschlaege([]);
        toast.error('Fehler beim Laden der Vorschläge', {
          description: err instanceof Error ? err.message : 'Unbekannter Fehler',
        });
      }
    } finally {
      if (!linkExistingCancelledRef.current) setLoadingVorschlaege(false);
    }
  }

  return (
    <div className="space-y-2" data-testid="mietwagen-crosslink-card">
      {/* ── Checkbox ── */}
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid="mietwagen-in-anspruch-checkbox"
          checked={!!mietwagenInAnspruch}
          disabled={saving}
          onChange={(e) => handleCheckboxChange(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 accent-amber-500"
        />
        <span className="text-[12px] font-medium text-gray-700">
          Mietwagen in Anspruch genommen
        </span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
      </label>

      {/* ── Mode: LINKED — header chip with deep link + KPI sub-section ── */}
      {mode === 'linked' && (
        <>
          <LinkedChip linked={linked} loading={loadingLinked} onNavigate={navigate} />
          {linked && (
            <MietwagenSubSection linked={linked} onNavigate={navigate} />
          )}

          {/* Unterschriften-Anfrage CTA — nur wenn vertrag_offen und noch keine Unterschrift */}
          {linked && linked.mietwagen_status === 'vertrag_offen' && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="mietwagen-signature-request-btn"
                disabled={saving}
                onClick={handleSignatureRequest}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                Unterschriften-Anfrage senden
              </button>
              {linked.unterschrift_anfrage_datum && (
                <span
                  data-testid="mietwagen-signature-request-date"
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                >
                  <CalendarClock className="w-3 h-3" />
                  Angefragt {new Date(linked.unterschrift_anfrage_datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                </span>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Mode: SUGGESTIONS — auto-match banner ── */}
      {mode === 'suggestions' && (
        <div
          data-testid="mietwagen-vorschlaege-banner"
          className="flex flex-col gap-2 px-3 sm:px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50"
        >
          <div className="flex items-center gap-2 text-[12px] font-semibold text-amber-900">
            <Car className="w-3.5 h-3.5" />
            {loadingVorschlaege ? 'Mietwagen-Vorschläge werden geladen…' : 'Mögliche Mietwagen-Akte gefunden'}
          </div>
          {!loadingVorschlaege && (
            <ul className="flex flex-col gap-1.5">
              {vorschlaege.map((v) => {
                const tagessatz = v.tagespreis != null
                  ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v.tagespreis) + '/Tag'
                  : null;
                return (
                  <li
                    key={v.mietwagen_akte_id}
                    data-testid="mietwagen-vorschlag-item"
                    className="flex items-center gap-2 flex-wrap text-[12px] text-amber-900"
                  >
                    <span className="font-mono font-semibold">
                      {v.mietwagen_akten_nummer ?? '—'}
                    </span>
                    {v.kunde_name && (
                      <span className="opacity-80">· {v.kunde_name}</span>
                    )}
                    {tagessatz && (
                      <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                        {tagessatz}
                      </span>
                    )}
                    {v.confidence === 'high' && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                        sicher
                      </span>
                    )}
                    <button
                      type="button"
                      data-testid="mietwagen-vorschlag-verknuepfen"
                      disabled={saving}
                      onClick={() => handleLinkVorschlag(v.mietwagen_akte_id)}
                      className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
                    >
                      <Link2 className="w-3 h-3" />
                      Verknüpfen
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {/* Still show action buttons below suggestions for manual fallback */}
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-amber-100">
            <button
              type="button"
              data-testid="mietwagen-create-new"
              onClick={handleCreateNew}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Mietwagen-Akte anlegen
            </button>
          </div>
        </div>
      )}

      {/* ── Loading: Vorschläge werden geladen (checkbox on, no link yet) ── */}
      {loadingVorschlaege && mode === 'unlinked_active' && (
        <div
          data-testid="mietwagen-vorschlaege-loading"
          className="inline-flex items-center gap-2 text-[12px] text-gray-500"
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Mietwagen-Vorschläge werden gesucht…
        </div>
      )}

      {/* ── Mode: UNLINKED_ACTIVE — action buttons ── */}
      {mode === 'unlinked_active' && !loadingVorschlaege && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="mietwagen-link-existing"
            disabled={saving}
            onClick={handleLinkExisting}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <Link2 className="w-3.5 h-3.5" />
            Mit bestehender Mietwagen-Akte verknüpfen
          </button>
          <button
            type="button"
            data-testid="mietwagen-create-new"
            onClick={handleCreateNew}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Mietwagen-Akte anlegen
          </button>
        </div>
      )}

      {/* ── DataCompletenessModal — shown when required fields are missing ── */}
      {showDataModal && (
        <DataCompletenessModal
          open={true}
          fehlend={showDataModal}
          akteId={gutachtenAkteId}
          onClose={() => setShowDataModal(null)}
          onSaved={async () => {
            setShowDataModal(null);
            await activateCheckbox();
          }}
          onProceedAnyway={async () => {
            setShowDataModal(null);
            await activateCheckbox();
          }}
        />
      )}
    </div>
  );
}

function LinkedChip({
  linked,
  loading,
  onNavigate,
}: {
  linked: LinkedMietwagenAkte | null;
  loading: boolean;
  onNavigate: (href: string) => void;
}) {
  if (loading && !linked) {
    return (
      <div
        data-testid="mietwagen-chip-loading"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-[12px] text-gray-500"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Mietwagen-Akte wird geladen...
      </div>
    );
  }
  if (!linked) return null;

  const identifier = aktenIdentifier(linked);
  if (!identifier) return null;

  const href = buildAkteHref('mietwagen', identifier);

  return (
    <a
      data-testid="mietwagen-chip-link"
      href={href}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(href);
      }}
      className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors group max-w-full"
    >
      <span className="w-6 h-6 rounded-md bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
        <Car className="w-3.5 h-3.5" />
      </span>
      <span className="flex items-center gap-1.5 min-w-0 flex-wrap">
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
          Mietwagen:
        </span>
        <span className="text-[12px] font-semibold">MietGo</span>
        <span className="text-[12px] opacity-60">/</span>
        <span className="text-[12px] font-mono font-semibold truncate">
          {identifier}
        </span>
        {linked.kunde_name && (
          <span className="text-[11px] opacity-70 truncate hidden sm:inline">
            · {linked.kunde_name}
          </span>
        )}
        {linked.mietwagen_status && (
          <StatusPill status={linked.mietwagen_status} />
        )}
      </span>
      <ExternalLink className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 flex-shrink-0" />
    </a>
  );
}

function MietwagenSubSection({
  linked,
  onNavigate,
}: {
  linked: LinkedMietwagenAkte;
  onNavigate: (href: string) => void;
}) {
  const identifier = aktenIdentifier(linked);
  const href = identifier ? buildAkteHref('mietwagen', identifier) : null;

  const kpi = berechneMietkosten(linked.mietbeginn, linked.mietende, linked.tagespreis);

  const fmtCurrency = (val: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);

  const hasKpis = kpi.mietdauerTage > 0;

  if (!hasKpis) return null;

  return (
    <div
      data-testid="mietwagen-sub-section"
      className="mt-1 px-3 py-2.5 rounded-lg border border-amber-100 bg-amber-50/50 space-y-2"
    >
      {hasKpis && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {kpi.mietdauerTage > 0 && (
            <div className="flex flex-col" data-testid="kpi-mietdauer">
              <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Mietzeit</span>
              <span className="text-[12px] font-semibold text-gray-800">{kpi.mietdauerTage} Tage</span>
            </div>
          )}
          {kpi.tagespreis != null && (
            <div className="flex flex-col" data-testid="kpi-tagessatz">
              <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Tagessatz</span>
              <span className="text-[12px] font-semibold text-gray-800">{fmtCurrency(kpi.tagespreis)}/Tag</span>
            </div>
          )}
          {kpi.gesamtkosten != null && (
            <div className="flex flex-col" data-testid="kpi-gesamtkosten">
              <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Mietkosten (ca.)</span>
              <span className="text-[12px] font-semibold text-amber-700">{fmtCurrency(kpi.gesamtkosten)}</span>
            </div>
          )}
        </div>
      )}
      {href && (
        <button
          type="button"
          data-testid="mietwagen-sub-section-link"
          onClick={() => onNavigate(href)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900 transition-colors"
        >
          Zur Mietwagen-Akte
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
